/* Copyright 2024 Schibsted Products & Technology AS. Licensed under the terms of the MIT license.
 * See LICENSE.md in the project root.
 */

'use strict';

/*
 * This file declares configs that are essentially part of how the SDK works and interacts with our
 * backend servers.
 *
 * What goes here?
 * - The configurations that are likely to change over time
 * - Constants that are otherwise obscure (for example 7000 for a TIMEOUT)
 *
 * What doesn't go here?
 * - options that the users of the SDK are supposed to set or provide as a parameter to functions
 *   and classes.
 */

/**
 * Core configuration used by the SDK
 * @type {Object}
 * @memberof core
 * @prop {object} ENDPOINTS - URL to some of the services that users of this SDK may interact with
 * @prop {object} ENDPOINTS.SPiD - SPiD endpoints
 * @prop {string} ENDPOINTS.SPiD.LOCAL - Local endpoint (for Identity team)
 * @prop {string} ENDPOINTS.SPiD.DEV - Dev environment (for Identity team)
 * @prop {string} ENDPOINTS.SPiD.PRE - Pre-production environment
 * @prop {string} ENDPOINTS.SPiD.PRO_SE - Production environment Sweden
 * @prop {string} ENDPOINTS.SPiD.PRO_NO - Production environment Norway
 * @prop {string} ENDPOINTS.SPiD.PRO_FI - Production environment Finland
 * @prop {string} ENDPOINTS.SPiD.PRO_DK - Production environment Denmark
 * @prop {object} ENDPOINTS.BFF - Endpoints used with new GDPR-compliant web flows
 * @prop {string} ENDPOINTS.BFF.LOCAL - Local endpoint (for Identity team)
 * @prop {string} ENDPOINTS.BFF.DEV - Dev environment (for Identity team)
 * @prop {string} ENDPOINTS.BFF.PRE - Pre-production environment
 * @prop {string} ENDPOINTS.BFF.PRO_SE - Production environment Sweden
 * @prop {string} ENDPOINTS.BFF.PRO_NO - Production environment Norway
 * @prop {string} ENDPOINTS.BFF.PRO_FI - Production environment Finland
 * @prop {string} ENDPOINTS.BFF.PRO_DK - Production environment Denmark
 * @prop {object} ENDPOINTS.SESSION_SERVICE - Endpoints to check global user session data
 * @prop {string} ENDPOINTS.SESSION_SERVICE.LOCAL - Local endpoint (for Identity team)
 * @prop {string} ENDPOINTS.SESSION_SERVICE.DEV - Dev environment (for Identity team)
 * @prop {string} ENDPOINTS.SESSION_SERVICE.PRE - Pre-production environment
 * @prop {string} ENDPOINTS.SESSION_SERVICE.PRO_SE - Production environment Sweden
 * @prop {string} ENDPOINTS.SESSION_SERVICE.PRO_NO - Production environment Norway
 * @prop {string} ENDPOINTS.SESSION_SERVICE.PRO_FI - Production environment Finland
 * @prop {string} ENDPOINTS.SESSION_SERVICE.PRO_DK - Production environment Denmark
 * @prop {string} ENDPOINTS.PROFILE_PAGES.LOCAL - Local endpoint (for Identity team)
 * @prop {string} ENDPOINTS.PROFILE_PAGES.DEV - Dev environment (for Identity team)
 * @prop {string} ENDPOINTS.PROFILE_PAGES.PRE - Pre-production environment
 * @prop {string} ENDPOINTS.PROFILE_PAGES.PRO_SE - Production environment Sweden
 * @prop {string} ENDPOINTS.PROFILE_PAGES.PRO_NO - Production environment Norway
 * @prop {string} ENDPOINTS.PROFILE_PAGES.PRO_FI - Production environment Finland
 * @prop {string} ENDPOINTS.PROFILE_PAGES.PRO_DK - Production environment Denmark
 */
const config = {
    ENDPOINTS: {
        SPiD: {
            LOCAL: 'http://id.localhost/',
            DEV: 'https://login.dev.identity.m10s.io/',
            // PRE
            PRE: 'https://login.pre.identity.m10s.io/',
            // PRO
            PRO_SE: 'https://login.vend.se/',
            PRO_NO: 'https://login.vend.no/',
            PRO_FI: 'https://login.vend.fi/',
            PRO_DK: 'https://login.vend.dk/',
        },
        BFF: {
            LOCAL: 'http://id.localhost/authn/',
            DEV: 'https://login.dev.identity.m10s.io/authn/',
            // PRE
            PRE: 'https://login.pre.identity.m10s.io/authn/',
            // PRO
            PRO_SE: 'https://login.vend.se/authn/',
            PRO_NO: 'https://login.vend.no/authn/',
            PRO_FI: 'https://login.vend.fi/authn/',
            PRO_DK: 'https://login.vend.dk/authn/',
        },
        SESSION_SERVICE: {
            LOCAL: 'http://session.id.localhost/',
            DEV: 'https://session.login.dev.identity.m10s.io/',
            // PRE
            PRE: 'https://session.login.pre.identity.m10s.io/',
            // PRO
            PRO_SE: 'https://session.login.vend.se/',
            PRO_NO: 'https://session.login.vend.no/',
            PRO_FI: 'https://session.login.vend.fi/',
            PRO_DK: 'https://session.login.vend.dk/',
        },
        PROFILE_PAGES: {
            LOCAL: 'http://id.localhost/',
            DEV: 'https://dev.profile-pages.m10s.io/',
            // PRE
            PRE: 'https://account.pre.identity.m10s.io/',
            // PRO
            PRO_SE: 'https://account.vend.se/',
            PRO_NO: 'https://account.vend.no/',
            PRO_FI: 'https://account.vend.fi/',
            PRO_DK: 'https://account.vend.dk/',
        },
    },
    NAMESPACE: {
        LOCAL: 'id.localhost',
        DEV: 'schibsted.com',
        // PRE
        PRE: 'schibsted.com',

        // PRO
        PRO_SE: 'schibsted.com',
        PRO_NO: 'spid.no',
        PRO_FI: 'schibsted.fi',
        PRO_DK: 'schibsted.dk',
    }
};

export const VALID_ENVS = ['DEV', 'PRE', 'PRO_NO', 'PRO_DK', 'PRO_SE', 'PRO_FI'];

export default config;
export const ENDPOINTS = config.ENDPOINTS;
export const NAMESPACE = config.NAMESPACE;

